/**
 * PASEK, KTÓRY SIĘ NIE MIEŚCI, DAJE SIĘ PRZESUNĄĆ — JEDEN MECHANIZM DLA CAŁEJ APLIKACJI.
 *
 * ═══ [zgłoszenie właściciela 2026-09-05, powtórzone] ═══
 * „KAŻDY TOOLBAR, KTÓRY SIĘ NIE MIEŚCI, UMOŻLIWIAJ PRZESUWANIE ELEMENTÓW PRZYCISKU, by dać swobodę
 * poruszania się na dowolnym urządzeniu."
 *
 * BYŁO ŹLE: `styles/global.css` obiecywał przewijanie tylko PIGUŁCE ZAKŁADEK (`.tabs`), a i to wyłącznie
 * palcem — `overflow-x: auto` na ekranie dotykowym przesuwa się gestem, ale myszą nie ma czego chwycić,
 * bo pasek przewijania jest ukryty (`scrollbar-width: none`). Sam PASEK (`.toolbar`) nie przewijał się
 * nigdy: gdy zakładki, pole szukania i filtry razem nie mieściły się w szerokości ekranu, nadmiar był
 * po prostu MALOWANY POZA krawędzią i nie dało się do niego dojechać żadnym gestem.
 * SKUTEK DLA UŻYTKOWNIKA: w /admin/access-control na węższym oknie ostatnia zakładka („Widoczność menu")
 * była ucięta w połowie napisu, a pole szukania wychodziło poza ekran — i nie było jak ich dosięgnąć.
 *
 * TERAZ — jedno miejsce, zero zmian w 106 arkuszach podstron:
 *  • pasek, którego zawartość jest szersza niż on sam, dostaje `data-przewija="1"` (CSS włącza wtedy
 *    przewijanie poziome i kursor „łapki"). Pasek, który się mieści, zostaje BEZ przewijania — inaczej
 *    `overflow` przyciąłby rozwijane listy stojące wewnątrz paska;
 *  • przewijać można trzema drogami: gestem palca (było), CIĄGNIĘCIEM MYSZĄ (nowe) i kółkiem myszy
 *    w poziomie (nowe — pionowe kółko nad przewijalnym paskiem przesuwa go w bok, bo w pasku nie ma
 *    czego przewijać w pionie);
 *  • pomiar jest ŻYWY: `ResizeObserver` na pasku i na jego rodzicu plus `MutationObserver` na treści,
 *    więc zakładka dokładająca filtry do slotu paska od razu włącza przewijanie.
 *
 * ⚠️ CIĄGNIĘCIE NIE MOŻE ZJEŚĆ KLIKNIĘCIA. Przeciągnięcie zaczyna się dopiero po przekroczeniu progu
 * (`PROG_PRZECIAGNIECIA` px). Jeżeli próg został przekroczony, najbliższe `click` jest połykane w fazie
 * przechwytywania — bez tego dojechanie do ostatniej zakładki KOŃCZYŁOBY SIĘ jej kliknięciem.
 *
 * ⚠️ POLA TEKSTOWE ZOSTAJĄ POLAMI. Ciągnięcie nie startuje na `input`, `textarea`, `select` ani
 * `[contenteditable]` — tam przeciąganie myszą zaznacza tekst i to jest właściwe zachowanie.
 *
 * Wyłączenie dla konkretnego paska: `data-pasek-bez-przewijania="1"` na jego elemencie.
 */

/** O tyle pikseli trzeba ruszyć myszą, żeby to był przesuw paska, a nie kliknięcie. */
export const PROG_PRZECIAGNIECIA = 4;

/** Zapas na błędy zaokrągleń przy skalowaniu strony — 1 px różnicy to nie jest przepełnienie. */
const ZAPAS_PX = 2;

/** Selektor pasków objętych mechanizmem: kontrakt paska i pigułka zakładek z `styles/global.css`. */
export const SELEKTOR_PASKOW = ".toolbar, .tabs, [data-przewijalny]";

/**
 * Czy element przepełnia się w poziomie. Wydzielone, żeby zapadka mogła to sprawdzić na podstawionych
 * wymiarach — jsdom nie liczy układu, więc `scrollWidth` i `clientWidth` są tam zawsze zerami.
 */
export function czyPrzepelniony(el) {
    if (!el) return false;
    return (el.scrollWidth || 0) - (el.clientWidth || 0) > ZAPAS_PX;
}

/**
 * Czy zdarzenie należy do WEWNĘTRZNEGO paska, który sam się przewija.
 *
 * ⚠️ Paski bywają zagnieżdżone: pigułka zakładek (`.tabs`) stoi WEWNĄTRZ paska (`.toolbar`), a oba
 * pasują do tego samego selektora. Bez tego warunku jedno pociągnięcie za zakładki przesuwałoby
 * jednocześnie pigułkę i cały pasek — dwa razy szybciej i w sposób nie do opanowania.
 * Reguła: obsługuje NAJBLIŻSZY przewijalny pasek pod wskaźnikiem, reszta ustępuje.
 */
export function czyObslugujeWewnetrzny(el, cel) {
    if (!cel || typeof cel.closest !== "function") return false;
    const najblizszy = cel.closest(SELEKTOR_PASKOW);
    return !!najblizszy && najblizszy !== el && el.contains(najblizszy) && czyPrzepelniony(najblizszy);
}

/** Czy w tym miejscu ciągnięcie ma ustąpić miejsca natywnemu zachowaniu (zaznaczanie tekstu, suwaki). */
export function czyPomijamyCiagniecie(cel) {
    if (!cel || typeof cel.closest !== "function") return false;
    return !!cel.closest("input, textarea, select, [contenteditable='true'], [role='slider']");
}

/**
 * Która krawędź paska coś jeszcze chowa: `start` (jest coś po prawej), `koniec` (coś po lewej),
 * `srodek` (po obu stronach). CSS ściemnia dokładnie tę krawędź, za którą coś zostało — podpowiedź
 * musi być PRAWDZIWA, bo stałe ściemnienie obu końców kłamie po dojechaniu do ostatniej zakładki.
 */
export function krawedzPaska(el) {
    const maxScroll = (el.scrollWidth || 0) - (el.clientWidth || 0);
    if (maxScroll <= ZAPAS_PX) return "";
    const x = el.scrollLeft || 0;
    if (x <= ZAPAS_PX) return "start";
    if (x >= maxScroll - ZAPAS_PX) return "koniec";
    return "srodek";
}

/** Ustawia (albo zdejmuje) znacznik przepełnienia i stan krawędzi. Zwraca aktualny stan. */
export function odswiezStanPaska(el) {
    if (!el) return false;

    /* ⚠ PŁULAPKA JAJKO-KURA — to przez nią mechanizm był martwy od dnia napisania.
       [zgłoszenie właściciela 2026-09-05, powtórzone krzykiem] „KAŻDY toolbar, który się nie
       mieści, umożliwiaj przesuwanie elementów — w dowolnym urządzeniu."

       Arkusze zawijają paski regułą `[class*="toolbar"]:not([data-przewija="1"]) { flex-wrap: wrap }`.
       Wykluczenie miało dawać pierwszeństwo paskowi przewijalnemu — ale znacznik `data-przewija`
       ustawia dopiero TEN pomiar, a zawinięty kontener flex NIGDY nie ma `scrollWidth > clientWidth`.
       Warunek włączenia wymagał więc stanu, który mógł powstać dopiero po włączeniu. Wynik: znacznik
       `data-przewijalny` stał na kilkunastu paskach i nie robił nic, a testy tego nie widziały,
       bo jsdom nie liczy układu.

       NAPRAWA U ŹRÓDŁA: pomiar nie pyta kaskady o zdanie — na czas mierzenia SAM wymusza jeden
       rząd. Gdy pasek się w nim nie mieści, zostaje w jednym rzędzie i dostaje znacznik (dalej
       robi swoje CSS z global.css); gdy się mieści, styl wraca dokładnie taki, jaki był, więc
       zawijanie tam, gdzie jest potrzebne, działa bez zmian. */
    const bylWlasny = el.style.flexWrap;
    el.style.flexWrap = "nowrap";
    const przepelniony = czyPrzepelniony(el);
    if (!przepelniony) el.style.flexWrap = bylWlasny;

    if (przepelniony) {
        el.setAttribute("data-przewija", "1");
        el.setAttribute("data-krawedz", krawedzPaska(el));
    } else {
        el.removeAttribute("data-przewija");
        el.removeAttribute("data-ciagnie");
        el.removeAttribute("data-krawedz");
    }
    return przepelniony;
}

/**
 * Podpina obsługę do JEDNEGO paska. Zwraca funkcję sprzątającą.
 * Idempotentne: drugie wywołanie na tym samym elemencie nie dokłada drugiego kompletu nasłuchów.
 */
export function podepnijPasek(el, { okno = typeof window !== "undefined" ? window : null } = {}) {
    if (!el || el.__paskiPrzewijalne) return () => {};
    if (el.getAttribute("data-pasek-bez-przewijania") === "1") return () => {};

    let startX = 0;
    let startScroll = 0;
    let wcisniety = false;
    let ciagniemy = false;

    const odswiez = () => odswiezStanPaska(el);

    const naDol = (e) => {
        // Tylko lewy przycisk / palec / pióro. Prawy przycisk otwiera menu kontekstowe.
        if (e.button !== undefined && e.button !== 0) return;
        if (!czyPrzepelniony(el)) return;
        if (czyPomijamyCiagniecie(e.target)) return;
        if (czyObslugujeWewnetrzny(el, e.target)) return;
        wcisniety = true;
        ciagniemy = false;
        startX = e.clientX;
        startScroll = el.scrollLeft;
    };

    const naRuch = (e) => {
        if (!wcisniety) return;
        const przesuniecie = e.clientX - startX;
        if (!ciagniemy) {
            if (Math.abs(przesuniecie) < PROG_PRZECIAGNIECIA) return;
            ciagniemy = true;
            el.setAttribute("data-ciagnie", "1");
            // Przechwycenie wskaźnika: ręka może wyjechać poza pasek i przesuw ma za nią nadążać.
            if (e.pointerId !== undefined && el.setPointerCapture) {
                try { el.setPointerCapture(e.pointerId); } catch { /* przeglądarka odmówiła — trudno */ }
            }
        }
        el.scrollLeft = startScroll - przesuniecie;
        // Bez tego przeglądarka zaczyna „przeciąganie" tekstu i zaznaczanie w poprzek paska.
        if (e.cancelable) e.preventDefault();
    };

    const naGore = (e) => {
        if (!wcisniety) return;
        wcisniety = false;
        el.removeAttribute("data-ciagnie");
        if (e && e.pointerId !== undefined && el.releasePointerCapture && el.hasPointerCapture?.(e.pointerId)) {
            try { el.releasePointerCapture(e.pointerId); } catch { /* nic */ }
        }
        if (!ciagniemy) return;
        ciagniemy = false;
        // Połknij DOKŁADNIE JEDNO kliknięcie, które przeglądarka wyśle po przeciągnięciu — inaczej
        // dojechanie do ostatniej zakładki kończyłoby się jej kliknięciem.
        const polknij = (ev) => {
            ev.stopPropagation();
            ev.preventDefault();
        };
        el.addEventListener("click", polknij, { capture: true, once: true });
        // Gdyby kliknięcie nie nadeszło (np. wskaźnik puszczony poza paskiem), nasłuch nie może zostać
        // i połknąć kliknięcia z następnej minuty.
        okno?.setTimeout(() => el.removeEventListener("click", polknij, { capture: true }), 0);
    };

    const naKolko = (e) => {
        if (!czyPrzepelniony(el)) return;
        if (czyObslugujeWewnetrzny(el, e.target)) return;
        // W pasku nie ma czego przewijać w pionie, więc pionowe kółko przesuwa go w bok.
        const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
        if (!delta) return;
        el.scrollLeft += delta;
        if (e.cancelable) e.preventDefault();
    };

    // Krawędź musi nadążać za przewijaniem — także tym palcem i kółkiem, które nie idzie przez `naRuch`.
    const naPrzewiniecie = () => {
        if (el.getAttribute("data-przewija") === "1") el.setAttribute("data-krawedz", krawedzPaska(el));
    };

    el.addEventListener("scroll", naPrzewiniecie, { passive: true });
    el.addEventListener("pointerdown", naDol);
    el.addEventListener("pointermove", naRuch);
    el.addEventListener("pointerup", naGore);
    el.addEventListener("pointercancel", naGore);
    el.addEventListener("wheel", naKolko, { passive: false });

    let obserwatorRozmiaru = null;
    let obserwatorTresci = null;
    if (okno && typeof okno.ResizeObserver === "function") {
        obserwatorRozmiaru = new okno.ResizeObserver(odswiez);
        obserwatorRozmiaru.observe(el);
        if (el.parentElement) obserwatorRozmiaru.observe(el.parentElement);
    }
    if (okno && typeof okno.MutationObserver === "function") {
        obserwatorTresci = new okno.MutationObserver(odswiez);
        obserwatorTresci.observe(el, { childList: true, subtree: true, characterData: true });
    }

    odswiez();

    const sprzataj = () => {
        el.removeEventListener("pointerdown", naDol);
        el.removeEventListener("pointermove", naRuch);
        el.removeEventListener("pointerup", naGore);
        el.removeEventListener("pointercancel", naGore);
        el.removeEventListener("wheel", naKolko);
        el.removeEventListener("scroll", naPrzewiniecie);
        obserwatorRozmiaru?.disconnect();
        obserwatorTresci?.disconnect();
        el.removeAttribute("data-przewija");
        el.removeAttribute("data-ciagnie");
        el.removeAttribute("data-krawedz");
        delete el.__paskiPrzewijalne;
    };

    el.__paskiPrzewijalne = sprzataj;
    return sprzataj;
}

/**
 * Włącza mechanizm dla CAŁEJ aplikacji: podpina istniejące paski i pilnuje tych, które dopiero powstaną
 * (zmiana trasy, otwarcie zakładki, portal z filtrami). Wołane raz, z korzenia aplikacji.
 * @returns {() => void} funkcja wyłączająca
 */
export function wlaczPrzewijaniePaskow({ korzen = typeof document !== "undefined" ? document.body : null,
    okno = typeof window !== "undefined" ? window : null } = {}) {
    if (!korzen) return () => {};

    const podpiete = new Set();

    const skanuj = () => {
        korzen.querySelectorAll(SELEKTOR_PASKOW).forEach(el => {
            if (podpiete.has(el)) return;
            podpiete.add(el);
            podepnijPasek(el, { okno });
        });
        // Paski, których już nie ma w drzewie, przestają nas obchodzić.
        podpiete.forEach(el => { if (!el.isConnected) podpiete.delete(el); });
    };

    skanuj();

    let obserwator = null;
    if (okno && typeof okno.MutationObserver === "function") {
        obserwator = new okno.MutationObserver(skanuj);
        obserwator.observe(korzen, { childList: true, subtree: true });
    }
    const naZmianeOkna = () => podpiete.forEach(el => { if (el.isConnected) odswiezStanPaska(el); });
    okno?.addEventListener("resize", naZmianeOkna);

    return () => {
        obserwator?.disconnect();
        okno?.removeEventListener("resize", naZmianeOkna);
        podpiete.forEach(el => el.__paskiPrzewijalne?.());
        podpiete.clear();
    };
}

export default wlaczPrzewijaniePaskow;
